import { Type, ShapeTypeEnumType, Position, Icon, PlaceTypeEnumType, } from '@wix/ambassador-seating-v1-seating-plan/types';
import { isDarkColor } from '@wix/panda-js-utils';
import { COLORS, SHAPE_FILL_COLOR } from '../constants';
import { IconType, LabelsPosition, SHAPE, } from '../interfaces';
import { degreesToRadians } from './utils';
import { getNumberingToSequence } from './plan';
import { createLabelGenerator } from './label-generator';
const getCategoryColorsMap = (categories) => {
    const categoryColorsMap = {};
    categories.forEach(category => {
        categoryColorsMap[category.id] = category.config.color;
    });
    return categoryColorsMap;
};
export const elementsToShapes = ({ plan, shouldUseCategories, onlyShapeIds }) => {
    const { categories, sections } = plan;
    const categoryColorsMap = shouldUseCategories ? getCategoryColorsMap(categories) : null;
    const include = (elementId) => (onlyShapeIds ? onlyShapeIds.includes(elementId) : true);
    return sections
        .flatMap((section) => section.elements.map((element) => include(element.id)
        ? {
            ...getElementConfig(element),
            ...getCategoryProps(element, categoryColorsMap),
            label: element.title,
            type: getType(element),
            capacity: element.capacity,
            startAt: element.sequencing?.startAt?.toString(),
            reverseDirection: element.sequencing?.reverseOrder,
            id: element.id,
            sectorLabel: section.default ? '' : section.title,
            reservationOptions: element.reservationOptions,
            groupId: element.elementGroupId,
            ...getPlaces(element, categoryColorsMap),
            ...getPlaceOverrides(element),
            ...getMultiRowProps(element),
        }
        : null))
        .filter(a => a);
};
export const getElementConfig = (element) => {
    const { x, y, zIndex, width, height, rotationAngle, fontSize, cornerRadius, seatSpacing, hideLabel, labelPosition, seatLayout, emptyTopSeatSpaces, text, color, fillColor, strokeColor, strokeWidth, opacity, icon, image, seatNumbering, } = element.uiProperties;
    const config = {
        x,
        y,
        sortId: zIndex,
        width,
        height,
        angle: degreesToRadians(rotationAngle),
        fontSize,
        cornerRadius,
        spacing: seatSpacing,
        hideLabel,
        labelPosition: getShapeLabelPosition(labelPosition),
        layout: seatLayout,
        empty: emptyTopSeatSpaces,
        text,
        color,
        fillColor,
        strokeColor,
        strokeWidth,
        opacity: opacity / 100,
        icon: getShapeIcon(icon),
        image: getShapeImage(image),
        sequence: getNumberingToSequence(seatNumbering),
    };
    return config;
};
export const getType = (element) => {
    switch (element.type) {
        case Type.AREA:
            return SHAPE.AREA;
        case Type.ROW:
            return SHAPE.ROW;
        case Type.ROUND_TABLE:
            return SHAPE.ROUND_TABLE;
        case Type.TABLE:
            return SHAPE.TABLE;
        case Type.SHAPE:
            return getShapeType(element.uiProperties?.shapeType);
        case Type.MULTI_ROW:
            return element.multiRowProperties.curved ? SHAPE.CURVED_MULTI_ROW : SHAPE.MULTI_ROW;
        default:
            return undefined;
    }
};
export const getShapeLabelPosition = (labelPosition) => {
    const map = {
        [Position.LEFT]: LabelsPosition.LEFT,
        [Position.RIGHT]: LabelsPosition.RIGHT,
        [Position.BOTH]: LabelsPosition.BOTH,
    };
    return map[labelPosition] ?? LabelsPosition.NONE;
};
export const getShapeType = (shapeType) => {
    const map = {
        [ShapeTypeEnumType.ELLIPSE]: SHAPE.CIRCLE,
        [ShapeTypeEnumType.ICON]: SHAPE.ICON,
        [ShapeTypeEnumType.IMAGE]: SHAPE.IMAGE,
        [ShapeTypeEnumType.LINE]: SHAPE.LINE,
        [ShapeTypeEnumType.RECTANGLE]: SHAPE.RECTANGLE,
        [ShapeTypeEnumType.TEXT]: SHAPE.TEXT,
    };
    return map[shapeType] ?? undefined;
};
// TODO: start using ambassador types once migrated out of config
export const getShapeIcon = (icon) => {
    const map = {
        [Icon.CHECKROOM]: IconType.checkroom,
        [Icon.DRINKS]: IconType.drinks,
        [Icon.ELEVATOR]: IconType.elevator,
        [Icon.ENTER]: IconType.enter,
        [Icon.EXIT]: IconType.exit,
        [Icon.FOOD]: IconType.food,
        [Icon.SMOKING]: IconType.smoking,
        [Icon.STAGE]: IconType.stage,
        [Icon.STAIRS]: IconType.stairs,
        [Icon.WC]: IconType.wc,
        [Icon.WC_MEN]: IconType.wcMen,
        [Icon.WC_WOMEN]: IconType.wcWomen,
    };
    return map[icon] ?? undefined;
};
export const getShapeImage = (image) => {
    if (!image) {
        return undefined;
    }
    return {
        id: image.id,
        originalWidth: image.width,
        originalHeight: image.height,
        uri: image.uri,
    };
};
const getMultiRowProps = (element) => {
    if (element.multiRowProperties) {
        const { verticalSequencing: { startAt, reverseOrder, rowNumbering }, rowSpacing, curveAngleFrom, curveAngleTo, curveRadius, seatAmount, } = element.multiRowProperties;
        const rows = element.multiRowProperties.curved ? getCurvedMultiRowRows(element) : getMultiRowRows(element);
        return {
            rowsStartAt: startAt,
            rowsReverseDirection: reverseOrder,
            spacingBetweenRows: rowSpacing,
            rowsSequence: getNumberingToSequence(rowNumbering),
            rows,
            placeOverrides: [], // overrides are in rows
            seatAmount,
            curveAngleFrom: degreesToRadians(curveAngleFrom),
            curveAngleTo: degreesToRadians(curveAngleTo),
            curveRadius,
        };
    }
    else {
        return {};
    }
};
const getCurvedMultiRowRows = (element) => {
    const rowLabelGenerator = createLabelGenerator({
        sequence: getNumberingToSequence(element.multiRowProperties.verticalSequencing.rowNumbering),
        startAt: element.multiRowProperties.verticalSequencing.startAt,
        reverseDirection: element.multiRowProperties.verticalSequencing.reverseOrder,
        capacity: element.multiRowProperties.rows.length,
    });
    let placeOverrideIndexOffset = 0;
    const rows = element.multiRowProperties.rows.map((elementRow, rowIndex) => {
        const overrides = {};
        const defaultLabel = rowLabelGenerator.getLabelByIndex(rowIndex);
        if (elementRow.title !== defaultLabel) {
            overrides.label = elementRow.title;
        }
        if (elementRow.sequencing.startAt !== element.sequencing.startAt) {
            overrides.startAt = elementRow.sequencing.startAt;
        }
        if (elementRow.sequencing.reverseOrder !== element.sequencing.reverseOrder) {
            overrides.reverseDirection = elementRow.sequencing.reverseOrder;
        }
        if (elementRow.uiProperties.labelPosition !== element.uiProperties.labelPosition) {
            overrides.labelPosition = getShapeLabelPosition(elementRow.uiProperties.labelPosition);
        }
        if (elementRow.uiProperties.seatNumbering !== element.uiProperties.seatNumbering) {
            overrides.sequence = getNumberingToSequence(elementRow.uiProperties.seatNumbering);
        }
        if (elementRow.uiProperties.seatAmount !== undefined &&
            elementRow.uiProperties.seatAmount !== element.multiRowProperties.seatAmount) {
            overrides.seatAmount = elementRow.uiProperties.seatAmount;
        }
        if (elementRow.uiProperties.curveAngleFrom !== undefined &&
            elementRow.uiProperties.curveAngleFrom !== element.multiRowProperties.curveAngleFrom) {
            overrides.curveAngleFrom = degreesToRadians(elementRow.uiProperties.curveAngleFrom);
        }
        if (elementRow.uiProperties.curveAngleTo !== undefined &&
            elementRow.uiProperties.curveAngleTo !== element.multiRowProperties.curveAngleTo) {
            overrides.curveAngleTo = degreesToRadians(elementRow.uiProperties.curveAngleTo);
        }
        const rowPlaceOverrides = (element.overrides ?? [])
            .filter(placeOverride => placeOverride.index >= placeOverrideIndexOffset &&
            placeOverride.index < placeOverrideIndexOffset + elementRow.capacity)
            .map(placeOverride => ({
            categoryId: placeOverride.categoryId,
            type: placeOverride.type,
            index: placeOverride.index - placeOverrideIndexOffset,
        }));
        placeOverrideIndexOffset += elementRow.capacity;
        return {
            id: elementRow.id,
            overrides,
            placeOverrides: rowPlaceOverrides,
        };
    });
    return rows;
};
const getMultiRowRows = (element) => {
    const rowLabelGenerator = createLabelGenerator({
        sequence: getNumberingToSequence(element.multiRowProperties.verticalSequencing.rowNumbering),
        startAt: element.multiRowProperties.verticalSequencing.startAt,
        reverseDirection: element.multiRowProperties.verticalSequencing.reverseOrder,
        capacity: element.multiRowProperties.rows.length,
    });
    let placeOverrideIndexOffset = 0;
    const rows = element.multiRowProperties.rows.map((elementRow, rowIndex) => {
        const overrides = { width: elementRow.uiProperties.width };
        if (elementRow.uiProperties.relativeX) {
            overrides.relativeX = elementRow.uiProperties.relativeX;
        }
        const defaultLabel = rowLabelGenerator.getLabelByIndex(rowIndex);
        if (elementRow.title !== defaultLabel) {
            overrides.label = elementRow.title;
        }
        if (elementRow.sequencing.startAt !== element.sequencing.startAt) {
            overrides.startAt = elementRow.sequencing.startAt;
        }
        if (elementRow.sequencing.reverseOrder !== element.sequencing.reverseOrder) {
            overrides.reverseDirection = elementRow.sequencing.reverseOrder;
        }
        if (elementRow.uiProperties.seatSpacing !== element.uiProperties.seatSpacing) {
            overrides.spacing = elementRow.uiProperties.seatSpacing;
        }
        if (elementRow.uiProperties.labelPosition !== element.uiProperties.labelPosition) {
            overrides.labelPosition = getShapeLabelPosition(elementRow.uiProperties.labelPosition);
        }
        if (elementRow.uiProperties.seatNumbering !== element.uiProperties.seatNumbering) {
            overrides.sequence = getNumberingToSequence(elementRow.uiProperties.seatNumbering);
        }
        const rowPlaceOverrides = (element.overrides ?? [])
            .filter(placeOverride => placeOverride.index >= placeOverrideIndexOffset &&
            placeOverride.index < placeOverrideIndexOffset + elementRow.capacity)
            .map(placeOverride => ({
            categoryId: placeOverride.categoryId,
            type: placeOverride.type,
            index: placeOverride.index - placeOverrideIndexOffset,
        }));
        placeOverrideIndexOffset += elementRow.capacity;
        return {
            id: elementRow.id,
            overrides,
            placeOverrides: rowPlaceOverrides,
        };
    });
    return rows;
};
const getCategoryProps = (element, categoryColorsMap) => {
    if (categoryColorsMap) {
        const categoryColor = categoryColorsMap[element.categoryId];
        const textColor = isDarkColor(categoryColor) ? COLORS.INVERTED_TEXT : COLORS.TEXT;
        const shouldHaveElementColors = element.type === Type.AREA || element.reservationOptions?.reserveWholeElement;
        return {
            categoryId: element.categoryId,
            shapeColor: shouldHaveElementColors ? categoryColor : null,
            shapeTextColor: shouldHaveElementColors ? textColor : null,
        };
    }
    else {
        return {
            categoryId: element.categoryId,
        };
    }
};
const getPlaceOverrides = (element) => element.overrides?.length ? { placeOverrides: element.overrides } : {};
const getPlaces = (element, categoryColorsMap) => {
    if (element.places?.length) {
        if (categoryColorsMap) {
            const elementPlaceOverrideMap = {};
            element.overrides?.forEach(override => {
                elementPlaceOverrideMap[override.index] = override;
            });
            return {
                places: element.places.map(place => {
                    const placeOverride = elementPlaceOverrideMap[place.index];
                    let categoryId = placeOverride ? placeOverride.categoryId : null;
                    // places before single seat assignment did not have categoryId assigned,
                    // if there is no place override - "migrate" these legacy places
                    if (element.categoryId && !placeOverride) {
                        categoryId = element.categoryId;
                    }
                    const seatColor = categoryColorsMap[categoryId] || SHAPE_FILL_COLOR;
                    return {
                        ...place,
                        categoryId,
                        type: place.type ?? PlaceTypeEnumType.STANDARD,
                        seatColor,
                        seatTextColor: isDarkColor(seatColor) ? COLORS.INVERTED_TEXT : COLORS.TEXT,
                    };
                }),
            };
        }
        else {
            return {
                places: element.places.map(place => ({
                    ...place,
                    type: place.type ?? PlaceTypeEnumType.STANDARD,
                    seatColor: SHAPE_FILL_COLOR,
                    seatTextColor: COLORS.TEXT,
                })),
            };
        }
    }
    else {
        return {};
    }
};
//# sourceMappingURL=elements-to-shapes.js.map